import { Glue42Core } from "@glue42/core";

import {
    Glue42Office,
} from "../../../glue4office";
import {attachmentsErrorMessage, getOlDefaultFolders, isOutlookEnabled} from "./index";

import {OlDefaultFolders} from "../types";

export const validateRecipients = (email: Glue42Office.Outlook.EmailParams | Glue42Office.Outlook.LocalEmailParams) => {
    const recipientProps = ["to", "cc", "bcc"];
    return Object.keys(email).reduce((prev: Glue42Office.Outlook.EmailParams, curr: string) => {
        if (recipientProps.indexOf(curr) >= 0) {
            const receiver = email[curr];
            if (!Array.isArray(receiver)) {
                prev[curr] = [receiver.toString()];
                return prev;
            } else {
                prev[curr] = [];
                receiver.forEach((contactEmail: string) => {
                    prev[curr].push(contactEmail.toString());
                });
            }
        }

        prev[curr] = email[curr];
        return prev;
    }, {});
};

export const validateAttachments = (attachments: Glue42Office.Outlook.AttachmentParams[] | string[]) => {
    if (!Array.isArray(attachments)) {
        throw new Error(attachmentsErrorMessage);
    }
    if (attachments.length === 0) {
        return attachments;
    }

    let isValid = true;
    const firstAtt = (attachments as Glue42Office.Outlook.AttachmentParams[])[0];
    if (firstAtt.data !== undefined || firstAtt.fileName !== undefined) {
        (attachments as Glue42Office.Outlook.AttachmentParams[]).forEach((att: Glue42Office.Outlook.AttachmentParams) => {
            if (typeof att.data !== "string" || typeof att.fileName !== "string") {
                window.console.warn(attachmentsErrorMessage);
                isValid = false;
            }
        });
    } else {
        (attachments as string[]).forEach((att: string) => {
            if (typeof att !== "string") {
                window.console.warn(attachmentsErrorMessage);
                isValid = false;
            }
        });
    }
    return isValid ? attachments : null;
};

export const validateDate = (date: Date | string | number, propertyName: string) => {
    const newDate = new Date(date as number);
    const dateUnix = newDate.getTime();
    if (isNaN(dateUnix)) {
        window.console.warn(`Invalid type of "${propertyName}", expected "Date" or unix timestamp number`);
        return null;
    }
    return date;
};

export const validatePriority = (priority: string) => {
    const listOfPriorities = ["low", "normal", "high"];
    if (typeof priority !== "string") {
        window.console.warn(`Invalid type of "priority", expected "string", one of: "low", "normal" or "high"`);
        return "normal";
    }
    if (listOfPriorities.indexOf(priority) < 0) {
        throw new Error(`Invalid type of "priority", expected "string", one of: "low", "normal" or "high"`);
    }
    return priority;
};

export const validateNewItemOptions = (options: Glue42Office.Outlook.NewEmailOptions | Glue42Office.Outlook.NewTaskOptions) => {
    if ((options as Glue42Office.Outlook.NewEmailOptions).onSent && typeof (options as Glue42Office.Outlook.NewEmailOptions).onSent !== "function") {
        throw new Error(`Invalid type of "onSent" callback, expected function`);
    }
    if ((options as Glue42Office.Outlook.NewTaskOptions).onSaved && typeof (options as Glue42Office.Outlook.NewTaskOptions).onSaved !== "function") {
        throw new Error(`Invalid type of "onSaved" callback, expected function`);
    }
    if (options.onCanceled && typeof options.onCanceled !== "function") {
        throw new Error(`Invalid type of "onCanceled" callback, expected function`);
    }
};

export const validateLocalEmailParameters = (agm: Glue42Core.AGM.API, localEmailParams: Glue42Office.Outlook.LocalEmailParams) => {
    if (!isOutlookEnabled(agm)) {
        throw new Error(`The method "createLocalEmail" is not available`);
    }
    if (!localEmailParams) {
        throw new Error(`"localEmailParams" of type object are mandatory`);
    }
    if (!localEmailParams.sender) {
        throw new Error(`The property "sender" in "localEmailParams" is mandatory`);
    } else if (typeof localEmailParams.sender !== "string") {
        throw new Error(`Invalid type of "sender", expected string`);
    }
    if (!localEmailParams.to) {
        throw new Error(`The property "to" in "localEmailParams" is mandatory`);
    } else if (typeof localEmailParams.to !== "string" && !Array.isArray(localEmailParams.to)) {
        throw new Error(`Invalid type of "to", expected string or string[]`);
    }
    if (!localEmailParams.subject) {
        throw new Error(`The property "subject" in "localEmailParams" is mandatory`);
    } else if (typeof localEmailParams.subject !== "string") {
        throw new Error(`Invalid type of "subject", expected string`);
    }
};

export const validateLocation = (location: Glue42Office.Outlook.Email | Glue42Office.Outlook.CommonOutlookFolder | string) => {
    if (typeof location !== "string" && !(location as Glue42Office.Outlook.Email).ids) {
        throw new Error("Invalid location");
    }
    if (typeof location === "string" && location.indexOf("$") === 0) {
        const defaultFolders: OlDefaultFolders = getOlDefaultFolders();
        if (typeof defaultFolders[location] !== "number") {
            throw new Error("Invalid location");
        }
    }
};

export const validateCalendarEventParameters = (
    agm: Glue42Core.AGM.API,
    action: string,
    event: Glue42Office.Outlook.T42Appointment | Glue42Office.Outlook.T42Meeting) => {
    if (!isOutlookEnabled(agm)) {
        const methodName = action === "track" ? "trackEmail" : "untrackEmail";
        throw new Error(`The method ${methodName} is not available`);
    }
    if (!event || (!event.ids || event.ids.length === 0)) {
        throw new Error("Invalid meeting / appointment");
    }
};

export const validateId = (id: Glue42Office.Outlook.T42Id): Glue42Office.Outlook.T42Id => {
    if (!id.systemName || !id.nativeId) {
        throw new Error(`Invalid type of conversationId, expected "{systemName: string, nativeId: string"`);
    }

    const systemName = id.systemName.toString();
    const nativeId = id.nativeId.toString();
    return {systemName, nativeId};
};
